import { ExternalLink, MessageSquare, RefreshCw, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminPageLayout from "../../components/AdminPageLayout";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useToast } from "../../contexts/ToastContext";
import {
  AdminCommentFilters,
  AdminProductComment,
  CommentProductType,
  CommentStatus,
  PaginatedAdminComments,
  productCommentService,
} from "../../services/productCommentService";
import { productSearchService } from "../../services/productSearchService";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: Array<{ value: "" | CommentStatus; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "DELETED", label: "Deleted" },
];

const PRODUCT_TYPE_OPTIONS: Array<{ value: "" | CommentProductType; label: string }> = [
  { value: "", label: "All products" },
  { value: "PORTFOLIO", label: "Portfolio" },
  { value: "BASKET", label: "Basket" },
];

const STATUS_BADGE: Record<CommentStatus, string> = {
  PENDING: "bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800",
  APPROVED: "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800",
  DELETED: "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
};

function formatDateTime(value: string): string {
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return "-";
  return new Date(ts).toLocaleString();
}

/**
 * Admin moderation page (PWA-2265): list every product comment across all
 * statuses (including DELETED), optionally filtered by status / product type /
 * product id, and soft-delete any comment or reply. Deleting a root also removes
 * its replies server-side, so the list is refetched after each delete.
 *
 * The list load is guarded by a monotonic request token so an out-of-order
 * resolution from a stale filter/page never overwrites a newer one
 * (CLAUDE.md section 6) -- mandatory here because the rows drive a destructive
 * delete and must always reflect the current selection.
 */
const AdminCommentsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<"" | CommentStatus>("");
  const [productTypeFilter, setProductTypeFilter] = useState<"" | CommentProductType>("");
  const [productIdInput, setProductIdInput] = useState("");
  const [appliedProductId, setAppliedProductId] = useState<number | undefined>(undefined);

  const [page, setPage] = useState(1);
  const [data, setData] = useState<PaginatedAdminComments | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<AdminProductComment | null>(null);
  const [deleting, setDeleting] = useState(false);

  // The comment a row-click is currently resolving a provider for, so we can show
  // per-row feedback and prevent a double navigation.
  const [openingId, setOpeningId] = useState<number | null>(null);

  const listReqRef = useRef(0);

  const loadComments = useCallback(async () => {
    const reqId = ++listReqRef.current;
    setLoading(true);
    setError(null);
    const filters: AdminCommentFilters = {
      status: statusFilter || undefined,
      productType: productTypeFilter || undefined,
      productId: appliedProductId,
    };
    try {
      const response = await productCommentService.listCommentsForModeration(filters, page, PAGE_SIZE);
      if (reqId !== listReqRef.current) return;
      setData(response);
    } catch (err) {
      if (reqId !== listReqRef.current) return;
      setData(null);
      setError(err instanceof Error ? err.message : "Failed to load comments");
    } finally {
      if (reqId === listReqRef.current) setLoading(false);
    }
  }, [statusFilter, productTypeFilter, appliedProductId, page]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Reset to page 1 whenever a filter changes so we never request a page that no
  // longer exists for the narrowed result set.
  const onStatusChange = (value: "" | CommentStatus) => {
    setStatusFilter(value);
    setPage(1);
  };
  const onProductTypeChange = (value: "" | CommentProductType) => {
    setProductTypeFilter(value);
    setPage(1);
  };
  const applyProductId = () => {
    const trimmed = productIdInput.trim();
    if (!trimmed) {
      setAppliedProductId(undefined);
      setPage(1);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 1) {
      toast.error("Product ID must be a positive integer.");
      return;
    }
    setAppliedProductId(parsed);
    setPage(1);
  };

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await productCommentService.adminDeleteComment(pendingDelete.id);
      toast.success(`Comment #${pendingDelete.id} deleted.`);
      setPendingDelete(null);
      await loadComments();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete comment", 0);
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, toast, loadComments]);

  // The detail route is keyed by providerId, which the comment payload does not
  // carry, so resolve it from the (admin, unrestricted) product search before
  // navigating to /positions/discover/{type}/{providerId}/{productId}.
  const openProduct = useCallback(
    async (comment: AdminProductComment) => {
      if (openingId !== null) return;
      setOpeningId(comment.id);
      try {
        const response = await productSearchService.searchAdminProducts({
          productType: comment.productType,
          productId: comment.productId,
          pageSize: 1,
        });
        const matchedProduct = response.items.find(p => p.productId === comment.productId);
        if (!matchedProduct) {
          toast.error(`Product ${comment.productType} #${comment.productId} was not found.`);
          return;
        }
        navigate(
          `/positions/discover/${comment.productType.toLowerCase()}/${matchedProduct.providerId}/${comment.productId}`
        );
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to open product");
      } finally {
        setOpeningId(null);
      }
    },
    [openingId, navigate, toast]
  );

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.totalItems ?? 0;

  const selectClass =
    "bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all";

  return (
    <AdminPageLayout
      title="Comments Moderation"
      description="Review and delete product comments across all statuses, including deleted."
      actions={
        <button
          onClick={loadComments}
          disabled={loading}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={e => onStatusChange(e.target.value as "" | CommentStatus)}
            className={selectClass}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by product type"
            value={productTypeFilter}
            onChange={e => onProductTypeChange(e.target.value as "" | CommentProductType)}
            className={selectClass}
          >
            {PRODUCT_TYPE_OPTIONS.map(opt => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            aria-label="Filter by product ID"
            type="number"
            min={1}
            placeholder="Product ID"
            value={productIdInput}
            onChange={e => setProductIdInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") applyProductId();
            }}
            className="w-32 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 px-3 py-2 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
          />
          <button
            onClick={applyProductId}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-rp-amber hover:bg-yellow-600 text-white transition-colors"
          >
            Apply
          </button>
          {!loading && !error && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {totalItems} comment{totalItems === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-rp-separator">
              <thead className="bg-gray-50 dark:bg-rp-surface-sunken">
                <tr>
                  {["ID", "Type", "Product", "Thread", "Alias", "User ID", "Comment", "Status", "Created", ""].map(header => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-rp-separator">
                {loading && items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center">
                      <RefreshCw className="w-6 h-6 mx-auto animate-spin text-gray-400 dark:text-gray-600" />
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && !error && (
                  <tr>
                    <td colSpan={10} className="px-4 py-16 text-center">
                      <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No comments match these filters.</p>
                    </td>
                  </tr>
                )}
                {items.map(comment => (
                  <tr
                    key={comment.id}
                    onClick={() => openProduct(comment)}
                    title="Open product detail page"
                    aria-busy={openingId === comment.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white whitespace-nowrap">
                      {comment.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {comment.productType}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-sm font-mono text-rp-amber">
                        {comment.productId}
                        {openingId === comment.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <ExternalLink className="w-3.5 h-3.5" />
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                        {comment.parentId == null ? "Root" : `Reply -> #${comment.parentId}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {comment.alias ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {comment.userId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-md">
                      <span className="line-clamp-2 break-words">{comment.body}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border ${STATUS_BADGE[comment.status]}`}
                      >
                        {comment.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDateTime(comment.createdAtUtc)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setPendingDelete(comment);
                        }}
                        disabled={comment.status === "DELETED"}
                        title={comment.status === "DELETED" ? "Already deleted" : "Delete comment"}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-semibold rounded-lg text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {data?.currentPage ?? page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages || loading}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => (deleting ? undefined : setPendingDelete(null))}
        onConfirm={confirmDelete}
        title="Delete Comment"
        message={
          pendingDelete && pendingDelete.parentId == null
            ? `Delete comment #${pendingDelete.id}? This is a root comment, so all of its replies will be deleted too. This cannot be undone.`
            : `Delete comment #${pendingDelete?.id ?? ""}? This cannot be undone.`
        }
        confirmText="Delete"
        isDestructive
        loading={deleting}
      />
    </AdminPageLayout>
  );
};

export default AdminCommentsPage;
